import {
  compositeResourceIdCodec,
  isResourceType,
  type ResourceType,
} from "../extractors/identity";

// Loose enough to take both a hydrated node and the app's GraphQL node, whose
// location fields are nullable rather than optional.
export type LineageResourceLocation = {
  resourceType: string;
  resourceId: string;
  kind: string | null;
  exists: boolean;
  locationData: {
    modelId?: string | null;
    sourceId?: string | null;
    flowId?: string | null;
    channelId?: string | null;
    subsetGroupId?: string | null;
  } | null;
};

type LineageRoute = (node: LineageResourceLocation) => string | null;

const MODEL_KIND_ROUTES: Record<string, string> = {
  parent: "schema/parent-models",
  related: "schema/related-models",
  event: "schema/events",
  realtimeEvent: "schema/realtime-events",
  retl: "models",
};

const byResourceId =
  (route: string): LineageRoute =>
  (node) =>
    `/${route}/${node.resourceId}`;

const noPage: LineageRoute = () => null;

// TODO: make this use a proper url builder when one becomes available
const LINEAGE_ROUTES: Record<ResourceType, LineageRoute> = {
  audience: byResourceId("audiences"),
  destination: byResourceId("destinations"),
  journey: byResourceId("journeys"),
  message: byResourceId("native-delivery/messages"),
  sync: byResourceId("syncs"),
  sync_template: byResourceId("templates/sync-templates"),
  trait: byResourceId("traits/active"),
  trait_template: byResourceId("traits/templates"),
  subset_category: byResourceId("governance/subset-categories"),

  model: (node) => {
    const route = node.kind ? MODEL_KIND_ROUTES[node.kind] : undefined;
    return route ? `/${route}/${node.resourceId}` : null;
  },

  model_column: ({ kind, locationData }) => {
    const modelId = locationData?.modelId;
    if (!modelId) {
      return null;
    }
    if (kind === "retl") {
      return `/models/${modelId}`;
    }
    return locationData?.sourceId
      ? `/schema-v2/view/columns?source=${locationData.sourceId}&id=${modelId}`
      : null;
  },

  journey_context_variable: ({ resourceId }) => {
    const [journeyId] = compositeResourceIdCodec(2).decode(resourceId);
    return `/journeys/${journeyId}`;
  },

  model_relationship: ({ locationData }) =>
    locationData?.modelId && locationData.sourceId
      ? `/schema-v2/view/relationships?source=${locationData.sourceId}&id=${locationData.modelId}`
      : null,

  decision_engine_flow: byResourceId("ai/flows"),
  decision_engine_feature_matrix: byResourceId(
    "ai/configuration/feature-matrices",
  ),
  decision_engine_channel: byResourceId("ai/configuration/channels"),

  audience_template: noPage,
  destination_rule: noPage,
  campaign: byResourceId("native-delivery/campaigns"),
  idr_graph: noPage,
  event_source: noPage,
  // Outcomes are edited on flow-scoped goal pages and send events inside a
  // channel drawer, so both link through the representative parent that
  // hydration carries in locationData.
  decision_engine_outcome: ({ resourceId, locationData }) =>
    locationData?.flowId
      ? `/ai/flows/${locationData.flowId}/configuration/goals/${resourceId}`
      : null,
  // A subset value page lives under its group, so the route needs both ids.
  subset: ({ resourceId, locationData }) =>
    locationData?.subsetGroupId
      ? `/governance/subset-categories/${locationData.subsetGroupId}/subsets/${resourceId}`
      : null,
  decision_engine_send_event: ({ locationData }) =>
    locationData?.channelId
      ? `/ai/configuration/channels/${locationData.channelId}`
      : null,
  // Message pages are flow-scoped, and the audience filter is edited on the
  // configuration tab.
  decision_engine_message: ({ resourceId, locationData }) =>
    locationData?.flowId
      ? `/ai/flows/${locationData.flowId}/messages/${resourceId}/configuration`
      : null,
};

/**
 * Workspace-relative path to a lineage node in the app, without the workspace
 * slug. Null when the resource is gone or nothing addresses it.
 */
export function lineageResourcePath(
  node: LineageResourceLocation,
): string | null {
  if (node.exists === false) {
    return null;
  }
  if (!isResourceType(node.resourceType)) {
    return null;
  }
  return LINEAGE_ROUTES[node.resourceType](node) ?? null;
}
